// Offline form queue using IndexedDB
// Queues POST/PUT/DELETE requests when offline, replays when back online

const DB_NAME = 'cattle-offline-queue';
const STORE_NAME = 'requests';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueue(method, url, data, headers) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add({
      method,
      url,
      data,
      headers,
      timestamp: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getQueue() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function remove(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

export async function flush() {
  const items = await getQueue();
  const results = [];
  for (const item of items) {
    try {
      // Re-read the CSRF cookie at replay time rather than trusting the value
      // captured when the request was queued — the token may have rotated
      // (re-login, refresh) during the time this item spent offline.
      const headers = { ...(item.headers || { 'Content-Type': 'application/json' }) };
      const currentCsrfToken = getCookie('csrf_access_token');
      if (currentCsrfToken) {
        headers['X-CSRF-TOKEN'] = currentCsrfToken;
      }
      await fetch(item.url, {
        method: item.method,
        headers,
        body: item.data ? JSON.stringify(item.data) : undefined,
        credentials: 'include',
      });
      await remove(item.id);
      results.push({ id: item.id, ok: true });
    } catch {
      results.push({ id: item.id, ok: false });
      break; // stop on first failure, still offline
    }
  }
  return results;
}

// Auto-flush when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    flush().then((results) => {
      if (results.length > 0) {
        const synced = results.filter(r => r.ok).length;
        if (synced > 0) {
          window.dispatchEvent(new CustomEvent('offline-sync', { detail: { synced } }));
        }
      }
    });
  });
}
